// @ts-nocheck
// Agent Portal (/cti2) — Topbar.
// Byte-faithful to src/features/cti/NavBar.tsx's actual styling for the
// shared cti.css classes it still uses (`nav-bar`, `cti-focus-theme-
// toggle`/`-tab`, `idle-workspace-tabs`/`idle-ws-tab` via IdleView's portal
// into #cti-topbar-workspace-slot) — the agent-identity block is its own
// Tailwind layout instead (see below), plus one addition this shell needs
// that NavBar.tsx doesn't carry: an account menu (My Account / Logout) —
// same Avatar+DropdownMenu pattern as src/components/layout/Header.tsx's
// account menu, trimmed to just those two items (no break-status dot, no
// impersonation-stack "switch back" case, no Appearance item) since this
// route is always a single agent session. There's no separate logo block in
// this header; the company's branded logo rides on the account-menu avatar
// circle itself (falling back to the agent's initials when the company has
// no logo set), so branding is present without a second identity element.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Sun,
  Monitor,
  Moon,
  User as UserIcon,
  LogOut,
  PanelRightClose,
  PanelRightOpen,
  Bell,
  AlertTriangle,
  Phone,
  Clock,
  ChevronsLeft,
  ChevronsRight,
  Headphones,
} from 'lucide-react';
import { MdWifiCalling } from 'react-icons/md';
import { useThemeStore } from '@/lib/theme/store';
import AgentContextStrip from './AgentContextStrip';
import AudioSettingsModal from './AudioSettingsModal';
import { useBranding, useLogoutMutation } from '@/features/auth/api';
import { useAuthedImageUrl } from '@/hooks/useAuthedImageUrl';
import { storageUrlOr } from '@/utilities/storageUrl';
import { logoutAsProfile } from '@/features/cti/softphone/loginAsProfile';
import { trackAppLogout } from '@/service/activityTracker';
import { useCallStore, isExitBlocked, getExitBlockReason } from '@/router/zustand/callStore';
import { toast } from 'sonner';
import { Storage, getUser, hasSwitchBackStack, switchBackUser } from '@/utilities/setting';
import { clearLegacySessionKeys } from '@/lib/auth/strategy';
import { BACKEND_API_BASE_URL, ROUTES, USER_ROLES } from '@/utilities/constant';
import { isTeamLeadUser } from '../acl';
import { initialsFromName } from '../utils/format';
import NetworkSpeedIndicator from '../network/NetworkSpeedIndicator';
import { formatSeconds } from '@/features/cti/ctiHelpers';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const THEME_ORDER = ['light', 'dark', 'system'];
const THEME_ICON = { light: Sun, dark: Moon, system: Monitor };
const THEME_LABEL = { light: 'Light', dark: 'Dark', system: 'System' };

function CtiThemeToggle() {
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const next = THEME_ORDER[(THEME_ORDER.indexOf(mode) + 1) % THEME_ORDER.length];
  const Icon = THEME_ICON[mode] || Sun;
  return (
    <button
      type="button"
      title={`Theme: ${THEME_LABEL[mode] || 'Light'} (click for ${THEME_LABEL[next]})`}
      aria-label="Toggle theme"
      onClick={() => setMode(next)}
      className="cti-focus-theme-tab"
    >
      <Icon size={13} />
    </button>
  );
}

// "My dashboard"/"Team dashboard" used to be the second tab here — removed
// now that the dashboard's own section tabs (Performance/Voicemails/...)
// are always visible in this same header (see the always-mounted
// AgentDashboardView/TeamDashboardView in AgentPortalPage.tsx) and clicking
// any one of them already switches into dashboard mode on its own. "Live
// call" stays as the one way back to the live-call view.
const VIEW_MODE_TABS = [
  { key: 'live', label: 'Live call' },
];

function ViewModeSwitch({ activeTab, onChange, isTeamLead }) {
  const tabs = VIEW_MODE_TABS.map((t) => ({
    ...t,
    label: isTeamLead && t.teamLeadLabel ? t.teamLeadLabel : t.label,
  }));
  return (
    <div className="cti-mode-switch" role="tablist" aria-label="View">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={activeTab === t.key}
          onClick={() => onChange(t.key)}
          className={`cti-mode-tab ap-tab-hover${activeTab === t.key ? ' active ap-tab-emboss' : ''}`}
        >
          <MdWifiCalling size={15} className="cti-mode-tab__icon" />
          {t.label}
        </button>
      ))}
    </div>
  );
}

export default function Topbar({
  userData,
  activeViewTab,
  onViewModeChange,
  onOpenAccount,
  onBeforeLogout,
  showDrawerToggle = false,
  drawerOpen = false,
  onToggleDrawer,
  unreadNotifications = 0,
  onToggleNotifications,
  pendingDispositionsCount = 0,
  onPendingDispositionsClick,
  isTeamLead: isTeamLeadProp = false,
  agentId,
  campaigns = [],
  showAgentContext = true,
}) {
  const queryClient = useQueryClient();
  const logoutMutation = useLogoutMutation();
  const topbarScrollerRef = useRef<HTMLDivElement | null>(null);
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
  // Steps to the next/previous item's own left edge (Groups/Campaigns/
  // Schedule chips AND the dashboard tabs together, in DOM order) rather
  // than a fixed pixel amount, so the buttons always land on an item
  // boundary. Position is computed via getBoundingClientRect rather than
  // offsetLeft since offsetLeft is relative to the nearest positioned
  // ancestor, which AgentContextStrip/DashboardSectionNav's own wrapper
  // elements don't reliably provide.
  const stepTopbarScroller = (dir: number) => {
    const scroller = topbarScrollerRef.current;
    if (!scroller) return;
    const scrollerLeft = scroller.getBoundingClientRect().left;
    // Excludes "Live call" (.cti-mode-tab) — it's sticky now (stays put
    // while everything else scrolls, per request "Live call hile na"), so
    // its rect no longer reflects a real scroll-target position once stuck.
    const targets = Array.from(scroller.querySelectorAll('button:not(.cti-mode-tab)')).map(
      (el) => el.getBoundingClientRect().left - scrollerLeft + scroller.scrollLeft,
    );
    if (!targets.length) return;
    const current = scroller.scrollLeft;
    const next = dir > 0
      ? targets.find((left) => left > current + 4) ?? targets[targets.length - 1]
      : [...targets].reverse().find((left) => left < current - 4) ?? targets[0];
    scroller.scrollTo({ left: next, behavior: 'smooth' });
  };
  // « only shows once there's actually somewhere left to step BACK to —
  // stays hidden at the start (Groups), reappears once scrolled off it.
  // » always shows (this row can always be stepped further right, or is
  // already short enough not to need stepping at all — either way it's
  // harmless to click). Watches both the scroller's own size (window
  // resize) and its content's size (Groups/Campaigns counts and the
  // dashboard's badge counts populate async after first paint).
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  useEffect(() => {
    const scroller = topbarScrollerRef.current;
    if (!scroller) return undefined;
    const update = () => setCanScrollLeft(scroller.scrollLeft > 4);
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(scroller);
    const mo = new MutationObserver(update);
    mo.observe(scroller, { childList: true, subtree: true, characterData: true });
    return () => {
      scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  // Groups/Campaigns now behave like the dashboard's own section tabs
  // (Performance, Leads, ...) instead of opening a popover — switch into
  // My-dashboard and scroll down to their section there, per request
  // ("jaise baki tab pr click krne pr niche container m show krte hai,
  // vaise Groups/Campaigns ko bhi"). Same deferred-scroll pattern as
  // DashboardSectionNav's tab click: the dashboard may still be CSS-hidden
  // this same tick, so the section has no layout yet — wait a couple of
  // frames for that to commit before scrolling to it.
  const scrollToDashboardSection = (sectionId: string) => {
    onViewModeChange?.('dashboard');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };
  const handleGroupsSectionClick = () => scrollToDashboardSection('groups');
  const handleCampaignsSectionClick = () => scrollToDashboardSection('campaigns');

  const profiles = userData?.agentData?.agentTelephony?.profile || [];
  const activeTelephonyProfile = useMemo(
    () => profiles.find((p) => (p.type === 'WEBRTC' || p.type === 'Soft Phone') && p.active_profile === true),
    [profiles],
  );
  const extension =
    activeTelephonyProfile?.number?.number ||
    profiles.find((p) => p.type === 'WEBRTC')?.number?.number ||
    profiles.find((p) => p.type === 'Soft Phone')?.number?.number;
  const mobileNumber = userData?.agentData?.agent_number || null;
  const agentName = userData?.agentData?.agent_name || userData?.username || 'Agent';

  // Online-time counter — moved here from the Dialer's own header, next to
  // the account/user icon, per request ("Shift Time Shown in dialer tab top
  // to header top right along with user icon").
  const [loginSeconds, setLoginSeconds] = useState(0);
  useEffect(() => {
    let ts = Storage.getItem('loginTimestamp');
    if (!ts) {
      ts = String(Date.now());
      Storage.setItem('loginTimestamp', ts, true);
    }
    const loginTs = parseInt(ts, 10);
    setLoginSeconds(Math.floor((Date.now() - loginTs) / 1000));
    const id = setInterval(() => {
      setLoginSeconds(Math.floor((Date.now() - loginTs) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const initials = initialsFromName(agentName);

  const brandUser = getUser();
  const branding = useBranding();
  const logoPath = brandUser?.effective_logo_path || null;
  const logoBlobPath = brandUser?.effective_logo_blob_path || null;
  const rawLogoUrl = logoPath
    ? storageUrlOr('logo', logoBlobPath, `${BACKEND_API_BASE_URL.replace('/api', '')}/${logoPath}`)
    : (branding.data?.logoUrl ?? null);
  const { src: brandLogoUrl } = useAuthedImageUrl(rawLogoUrl);
  const isTeamLead = isTeamLeadProp || isTeamLeadUser(userData);

  const handleLogout = async () => {
    // Was completely unguarded — an agent with an active call OR an open/
    // pending disposition (wrap-up not over yet) could log out mid-flow
    // with no warning at all, unlike UserProfilePage.tsx's own logout
    // button, which already checks this.
    if (isExitBlocked()) {
      toast.error(getExitBlockReason() || 'Finish your current call/disposition before logging out.');
      return;
    }
    trackAppLogout({
      profile_type: (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('activeLoginType')) || null,
      from: 'agent_portal',
    });

    const wasImpersonating = hasSwitchBackStack();
    try { await onBeforeLogout?.(); } catch { /* noop — logout proceeds regardless */ }
    try {
      const userId = brandUser?.id;
      const activeType = sessionStorage.getItem('activeLoginType');
      if (userId && activeType) {
        useCallStore.getState().setProfileLogoutPending(true);
        await logoutAsProfile(userId, activeType).catch(() => {});
      }
    } catch { /* noop */ }
    try {
      if (typeof window !== 'undefined' && typeof window.__ctiReleaseAllMedia === 'function') {
        window.__ctiReleaseAllMedia();
      }
    } catch { /* noop */ }
    try { await logoutMutation.mutateAsync(undefined); } catch { /* proceed to login regardless */ }
    try {
      sessionStorage.removeItem('activeLoginType');
      localStorage.removeItem('activeLoginType');
      Storage.removeItem('loginTimestamp');
      useCallStore.getState().setWebrtcPhoneStartTime('');
    } catch { /* noop */ }
    queryClient.clear();

    if (wasImpersonating) {
      const restored = await switchBackUser();
      if (restored) {
        window.location.href = `/${ROUTES.DASHBOARD}`;
        return;
      }
      clearLegacySessionKeys();
    }

    window.location.replace(`/${ROUTES.LOGIN}`);
  };

  return (
    <div
      className="nav-bar"
      style={{
        // Squared off on the bottom only in dashboard mode, where the
        // content card below now sits flush against this header with no
        // gap (see AgentDashboardView/TeamDashboardView's -mt-2) — with
        // both this card's bottom corners and that card's top corners
        // rounded, the seam where they touch read as a pinched notch
        // instead of one continuous edge. Live-call view still has its
        // usual gap below, so it keeps all four corners rounded.
        borderRadius: activeViewTab === 'dashboard' ? 'var(--radius, 10px) var(--radius, 10px) 0 0' : 'var(--radius, 10px)',
        flexShrink: 0,
      }}
    >
      {/* My-dashboard's own section tabs (Performance/Voicemails/Leads/...)
       * portal in here instead of rendering inline inside
       * AgentDashboardView/TeamDashboardView — moved up into this same
       * header row instead of a separate strip that scrolled away with
       * the page. DashboardSectionNav's own scroll-spy/click-to-jump logic
       * is unaffected: it only depends on the scrollRootRef and
       * document.getElementById(sectionId) it's given as props, never on
       * where its own <nav> happens to sit in the DOM. Always rendered now
       * (not just in dashboard mode) — AgentDashboardView/TeamDashboardView
       * stay mounted permanently (CSS-hidden, not unmounted, while viewing
       * Live call) precisely so this stays populated and clickable from
       * either view; clicking a tab switches into dashboard mode itself. */}
      {/* Groups/Campaigns/Schedule (AgentContextStrip, moved up here from
       * above the live-call card stack) + the dashboard tabs share ONE
       * scroller with ONE pair of step buttons — not two separate scroll
       * regions competing for the same row — so "next" steps through
       * every item in this header, Groups included, and dragging the one
       * scrollbar underneath can always get back to the very first one. */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => stepTopbarScroller(-1)}
          aria-label="Scroll left"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/85"
        >
          <ChevronsLeft className="h-3.5 w-3.5" />
        </button>
      )}
      <div ref={topbarScrollerRef} className="ap-dashnav-scroller flex min-w-0 flex-1 items-center gap-2.5 overflow-x-auto scroll-smooth">
        {/* Groups, Campaigns, Live call (centered between the context chips
         * and the dashboard tabs — "live call center m hi le ao"), then
         * Performance onward, then Schedule last (portaled into
         * ap-topbar-schedule-slot below, same technique the dashboard tabs
         * themselves already use for ap-topbar-tabs-slot). */}
        {showAgentContext && (
          <AgentContextStrip
            agentId={agentId}
            userData={userData}
            campaigns={campaigns}
            className="flex shrink-0 items-center gap-1.5"
            onGroupsClick={handleGroupsSectionClick}
            onCampaignsClick={handleCampaignsSectionClick}
          />
        )}
        {onViewModeChange && (
          <ViewModeSwitch activeTab={activeViewTab} onChange={onViewModeChange} isTeamLead={isTeamLead} />
        )}
        <div id="ap-topbar-tabs-slot" className="ap-topbar-tabs-slot flex shrink-0 items-center" />
        <div id="ap-topbar-schedule-slot" className="flex shrink-0 items-center" />
      </div>
      <button
        type="button"
        onClick={() => stepTopbarScroller(1)}
        aria-label="Scroll right"
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/85"
      >
        <ChevronsRight className="h-3.5 w-3.5" />
      </button>
      {/* Mobile-only line break: cti2.css moves the tab scroller below it. */}
      <span aria-hidden className="ap-topbar-row-break hidden" />

      {/* Online-time pill removed from here — now shown in the Dialer
       * itself (both its expanded header and collapsed rail), so it no
       * longer needs a second, redundant copy in this header. */}

      {/* Same 7×7 neumorphic square the Dialer's own collapse/expand
        * toggles use (Dialer.tsx / DialerCollapsedRail.tsx), so it reads as
        * the same class of control — a panel toggle — rather than a third
        * button idiom in this bar. */}
      {showDrawerToggle && (
        <button
          type="button"
          onClick={onToggleDrawer}
          title={drawerOpen ? 'Collapse panel' : 'Expand panel'}
          aria-label={drawerOpen ? 'Collapse panel' : 'Expand panel'}
          aria-pressed={drawerOpen}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-2xl bg-card text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground"
        >
          {drawerOpen ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
        </button>
      )}

      {pendingDispositionsCount > 0 && (
        <button
          type="button"
          onClick={onPendingDispositionsClick}
          title={`${pendingDispositionsCount} skipped disposition${pendingDispositionsCount > 1 ? 's' : ''} still pending`}
          aria-label={`${pendingDispositionsCount} pending dispositions`}
          className="dpc-blink flex h-7 shrink-0 items-center gap-1.5 border border-transparent px-2 text-[11px] font-semibold"
        >
          <AlertTriangle size={13} />
          {pendingDispositionsCount} pending
        </button>
      )}

      {/* Logged-in user's upload / download speed (green / orange / red). */}
      <NetworkSpeedIndicator />

      {onToggleNotifications && (
        <button
          type="button"
          onClick={onToggleNotifications}
          title="Notifications"
          aria-label="Notifications"
          className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-2xl bg-card text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground"
        >
          <Bell size={14} />
          {unreadNotifications > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
              {unreadNotifications > 99 ? '99+' : unreadNotifications}
            </span>
          )}
        </button>
      )}

      <CtiThemeToggle />

      {/* Agent identity (name/extension/mobile) — moved here, right after
       * the brightness/theme toggle, out of the scrollable left side of
       * this header. No avatar circle here anymore — it duplicated the
       * account-menu trigger's own avatar right next to it. */}
      <div className="flex min-w-0 max-w-[220px] shrink-0 items-center gap-2.5 max-lg:flex-1 md:max-w-[300px]">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold tracking-tight text-foreground" title={agentName}>
              {agentName}
            </span>
            {extension ? (
              <span className="inline-flex shrink-0 items-center rounded-full border border-border bg-muted/50 px-1.5 py-px font-mono text-[10px] font-semibold text-muted-foreground">
                x{extension}
              </span>
            ) : null}
          </div>
          {mobileNumber ? (
            <div className="flex items-center gap-1 truncate text-[12.5px] leading-tight text-muted-foreground">
              <Phone size={10} className="shrink-0 text-green-500" />
              <span className="tabular-nums">{mobileNumber}</span>
            </div>
          ) : null}
        </div>
      </div>

      <span
        className="flex shrink-0 items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-1 text-[11px] font-medium text-muted-foreground"
        title="Online Time - how long this user has been logged in"
      >
        <Clock size={11} />
        <span className="font-mono tabular-nums">{formatSeconds(loginSeconds)}</span>
      </span>

      {/* Same trigger/menu Header.tsx uses for the call-centre-agent role's
       * account menu on /dashboard — kept in normal flow (not the
       * position:fixed `.cti-focus-theme-tab`) and given an explicit
       * z-index above this route's `.ap-shell` (z-[1000]); Radix portals
       * DropdownMenuContent to <body> with a default z-50, which was
       * rendering the menu behind the fullscreen CTI shell — invisible on
       * open even though the trigger itself was fine. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            title={agentName}
            aria-label="Account menu"
            style={{ width: "50px", height: "50px", marginTop: "-10px" }}
            className="h-9 w-9 shrink-0 rounded-full border border-border/50 bg-card p-0 shadow-sm hover:bg-accent"
          >
            <Avatar className={`h-8 w-8 ${brandLogoUrl ? 'bg-white' : ''}`} style={{ height: "100%", width: "100%" }}>
              {brandLogoUrl ? <AvatarImage src={brandLogoUrl} alt="" className="object-contain" style={{ objectFit: 'cover', borderRadius: "50%", width: "100%", height: "100%" }}/> : null}
              <AvatarFallback className="bg-primary text-[10px] font-bold text-primary-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="z-[1100] w-56">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-xs font-bold text-primary-foreground">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground">{agentName}</div>
              <div className="truncate text-[11px] text-muted-foreground">Signed in</div>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onOpenAccount} className="cursor-pointer gap-2.5">
            <UserIcon className="h-4 w-4 text-muted-foreground" />
            My Account
          </DropdownMenuItem>
          <DropdownMenuItem
            // Next tick: let the Radix menu finish closing first, otherwise
            // its focus/pointer-events cleanup fights the dialog's and the
            // page can stay unclickable after the dialog closes.
            onSelect={() => setTimeout(() => setAudioSettingsOpen(true), 0)}
            className="cursor-pointer gap-2.5"
          >
            <Headphones className="h-4 w-4 text-muted-foreground" />
            Audio settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer gap-2.5 text-destructive focus:bg-destructive/10 focus:text-destructive">
            <LogOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AudioSettingsModal open={audioSettingsOpen} onOpenChange={setAudioSettingsOpen} />
    </div>
  );
}
